import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PublicLayout from "@/components/PublicLayout";
import { api, formatApiError } from "@/lib/api";
import { useLocale, pickLocalized } from "@/contexts/LocaleContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Loader2, Check, AlertTriangle, Clock, Users, MapPin, Calendar as CalIcon, Download } from "lucide-react";

function formatLong(iso, locale) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(locale === "hr" ? "hr-HR" : "en-GB", {
      weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Zagreb",
    }).format(new Date(iso));
  } catch { return iso; }
}

function icsForBooking(b, settings) {
  const fmt = (iso) => iso.replace(/[-:]/g, "").split(".")[0] + "Z";
  const desc = `Booking ${b.code} for ${b.party_size} at King's Caffe Bar`;
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//KingsCafe//Booking//EN",
    "BEGIN:VEVENT",
    `UID:${b.code}@caffekings.com`,
    `DTSTAMP:${fmt(new Date().toISOString())}`,
    `DTSTART:${fmt(b.starts_at)}`,
    `DTEND:${fmt(b.ends_at)}`,
    `SUMMARY:King's Café — Rezervacija ${b.code}`,
    `DESCRIPTION:${desc}`,
    `LOCATION:${settings?.address?.hr || ""}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\n");
}

export default function BookingConfirm() {
  const [params] = useSearchParams();
  const code = params.get("code");
  const sessionId = params.get("session_id");
  const isMock = params.get("mock") === "1";
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { settings } = useSettings();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (!code) {
      setError("Missing booking code.");
      setLoading(false);
      return;
    }
    const fetchBooking = async () => {
      try {
        const { data } = await api.get(`/public/booking/${code}`);
        setBooking(data);
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [code]);

  // Polling: if returned from stripe with session_id, poll until paid/failed
  useEffect(() => {
    if (!sessionId || isMock) return;
    let attempts = 0;
    setPolling(true);
    const id = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/public/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || data.payment_status === "failed" || attempts >= 8) {
          clearInterval(id);
          setPolling(false);
          // refresh booking
          const { data: b } = await api.get(`/public/booking/${code}`);
          setBooking(b);
        }
      } catch {
        clearInterval(id);
        setPolling(false);
      }
    }, 2500);
    return () => clearInterval(id);
  }, [sessionId, code, isMock]);

  if (loading) {
    return (
      <PublicLayout pageTitle="…" testid="booking-confirm-loading">
        <div className="py-24 flex justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      </PublicLayout>
    );
  }
  if (error || !booking) {
    return (
      <PublicLayout pageTitle={t("booking_page.title")} testid="booking-confirm-error">
        <section className="max-w-md mx-auto px-5 sm:px-8 py-24 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-destructive mb-3" />
          <h1 className="font-display text-3xl">Rezervacija nije pronađena</h1>
          <p className="text-muted-foreground mt-2 text-sm">{error || "Provjerite kod ili se obratite osoblju."}</p>
          <Link to="/booking" className="inline-block mt-6 text-primary hover:underline">
            ← {t("nav.booking")}
          </Link>
        </section>
      </PublicLayout>
    );
  }

  const isPaid = booking.payment_status === "paid";
  const tablesLabels = (booking.tables || []).map((t) => t.label).join(" + ");

  const downloadIcs = () => {
    const blob = new Blob([icsForBooking(booking, settings)], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kingscafe-${booking.code}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PublicLayout pageTitle={`Booking ${booking.code}`} testid="booking-confirm-page">
      <section className="max-w-2xl mx-auto px-5 sm:px-8 py-16">
        <div className="inline-flex p-4 rounded-full border border-primary/40 bg-primary/5 mb-5">
          <Check className="w-8 h-8 text-primary" />
        </div>
        <p className="text-xs uppercase tracking-[0.3em] text-primary/80">— {t("nav.booking")}</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-3" data-testid="booking-confirm-title">
          Rezervacija potvrđena
        </h1>
        <p className="text-muted-foreground mt-3 text-sm">
          Vaš kod: <span className="text-primary font-medium" data-testid="booking-confirm-code">{booking.code}</span>
        </p>

        {polling && (
          <div className="mt-6 border border-primary/40 bg-primary/5 rounded-lg p-3 text-sm flex items-center gap-2" data-testid="booking-polling">
            <Loader2 className="w-4 h-4 animate-spin" /> Provjeravam status plaćanja…
          </div>
        )}

        <div className="border border-border rounded-2xl bg-card p-6 mt-8 space-y-4" data-testid="booking-confirm-summary">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-primary mb-1 flex items-center gap-1.5"><CalIcon className="w-3 h-3" /> Kada / When</div>
              <div>{formatLong(booking.starts_at, locale)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-primary mb-1 flex items-center gap-1.5"><Users className="w-3 h-3" /> Osoba</div>
              <div>{booking.party_size}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-primary mb-1 flex items-center gap-1.5"><Clock className="w-3 h-3" /> Stol(ovi)</div>
              <div>{tablesLabels || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-primary mb-1">Status</div>
              <div className={isPaid ? "text-primary" : ""} data-testid="booking-confirm-status">
                {booking.status}{booking.payment_status !== "none" ? ` · ${booking.payment_status}` : ""}
              </div>
            </div>
          </div>
          {settings?.address && (
            <div className="border-t border-border pt-4 text-sm">
              <div className="text-xs uppercase tracking-widest text-primary mb-1 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Gdje / Where</div>
              <div className="text-muted-foreground">{pickLocalized(settings.address, locale)}</div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <button onClick={downloadIcs} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/40 text-primary hover:bg-primary/10 text-sm" data-testid="booking-confirm-ics">
            <Download className="w-4 h-4" /> Dodaj u kalendar / Add to calendar
          </button>
          <Link to="/" className="inline-flex items-center px-4 py-2 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/40 text-sm">
            Natrag / Back
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
